"use client";

import { CustomLink } from "@repo/design-system/components/ui/custom-link";
import { useReferrerStorage } from "@repo/design-system/hooks/use-job-pipeline";
import { useLocale } from "@repo/design-system/hooks/use-locale";
import {
  isOcaApplyLink,
  processApplyUrl,
} from "@repo/design-system/shared/eon/apply-url-helpers";
import type { TranslationsMap } from "@repo/design-system/types/language";
import { cn } from "@repo/design-system/utils/cn";
import { Children, type ReactNode, useEffect, useMemo, useState } from "react";
import { useRegistry } from "~/components/registry/registry-provider";
import useUCConsent from "~/hooks/use-uc-consent";
import { useLanguage } from "~/translations/loader/use-language";
import type { Job, JobData, SimilarJob } from "~/types/job";
import { type ButtonPropsAsLink, buttonVariants } from "./button-eon";

interface ApplyButtonProps extends ButtonPropsAsLink {
  blank?: boolean;
  children?: ReactNode;
  customUrl?: string;
  direct?: boolean;
  job: Job | JobData | SimilarJob;
}

export function ApplyButton({
  job,
  className,
  customUrl,
  blank = true,
  children,
  variant = "secondary",
  size,
  direct,
  ...props
}: ApplyButtonProps) {
  const t = useLanguage();
  const locale = useLocale();
  const { ui } = useRegistry();
  const [referrer] = useReferrerStorage();
  const { consentStatus } = useUCConsent("Google Analytics");

  const rawUrl = getApplicationUrl(customUrl, direct, job);
  const [processedUrl, setProcessedUrl] = useState(rawUrl);

  const source = "data" in job ? job.data.source : job.source;
  const postingLanguages =
    "data" in job ? job.data.postingLanguages : job.postingLanguages;

  useEffect(() => {
    if (!rawUrl || rawUrl.length <= 2) {
      return;
    }

    const gaId = consentStatus
      ? (document.cookie
          .split("; ")
          .find((row) => row.startsWith("_ga="))
          ?.split("=")?.[1] ?? null)
      : null;

    setProcessedUrl(
      processApplyUrl(rawUrl, {
        source,
        addPipeline: true,
        referrer: referrer || undefined,
        gaId,
        currentSearchParams: new URLSearchParams(window.location.search),
        currentLocale: locale,
        postingLanguages,
      })
    );
  }, [rawUrl, consentStatus, referrer, source, locale, postingLanguages]);

  const ocaLink = useMemo(
    () => (processedUrl ? isOcaApplyLink(processedUrl) : false),
    [processedUrl]
  );

  if (!processedUrl || processedUrl.length <= 2) {
    return null;
  }

  if (direct && !ui.showQuickApply) {
    return null;
  }

  const label = getLabel({
    applicationUrl: processedUrl,
    direct,
    childrenCount: Children.count(children) ?? 0,
    t,
    source,
  });

  // Direct mode with OCA link: use native <a> tag (OCA hash routing breaks with Next.js Link)
  if (direct && ocaLink) {
    if ("data" in job && job.data.idClient === "243650") {
      return null;
    }
    return (
      <a
        className={cn(buttonVariants({ variant, size, className }))}
        data-source="joblc"
        href={processedUrl}
        rel="noreferrer"
        target="_blank"
        {...props}
      >
        {label}
        {children}
      </a>
    );
  }

  return (
    <CustomLink
      blank={blank}
      className={cn(buttonVariants({ variant, size, className }))}
      data-source="joblc"
      href={processedUrl}
      passParams={!direct}
      {...props}
    >
      {label}
      {children}
    </CustomLink>
  );
}

function getApplicationUrl(
  customUrl: string | undefined,
  direct: boolean | undefined,
  job: Job | JobData | SimilarJob
) {
  if (customUrl) {
    return customUrl;
  }

  if (direct && "data" in job && job.data.applicationUrlDirect) {
    return job.data.applicationUrlDirect;
  }

  if ("data" in job) {
    return job.data.applicationUrl;
  }

  return job.applicationUrl;
}

function getLabel({
  applicationUrl,
  direct,
  childrenCount,
  t,
  source,
}: {
  applicationUrl: string;
  direct: boolean | undefined;
  childrenCount: number;
  t: TranslationsMap;
  source?: string;
}) {
  if (direct && childrenCount === 0) {
    return t.jobad_applyNow_Direct;
  }

  if (applicationUrl.startsWith("mailto:") && childrenCount === 0) {
    return t.apply_now;
  }
  if (
    (source === "avacon_manual_jobs" || source === "manual_jobs") &&
    childrenCount === 0
  ) {
    return t.apply_now;
  }

  if (childrenCount === 0) {
    return t.jobad_applyNow;
  }

  return null;
}
